"use client";

import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ChevronsUpDown, Languages, LogOut, User } from "lucide-react";
import NoticeManager from "src/components/Notice/NoticeManager";
import { Avatar, AvatarFallback, AvatarImage } from "src/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "src/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "src/components/ui/sidebar";
import useHilosStore from "src/hooks/useHilosStore";
import useUser from "src/hooks/useUser";
import { getProfileAvatarImage, userToString } from "src/Helpers";
import { buildRoute } from "src/router/router";

export function NavHeader() {
  const { isMobile } = useSidebar();
  const { session, updateSession } = useHilosStore();
  const { logout } = useUser();
  const { t, i18n } = useTranslation();

  const [name, email, initials, avatar] = useMemo(() => {
    if (!session) {
      return [];
    }
    const fullName = userToString(session);

    return [
      fullName,
      session.email,
      fullName.slice(0, 2).toUpperCase(),
      getProfileAvatarImage(session),
    ];
  }, [session]);

  const language = useMemo(() => {
    const result = ["en", "es", "pt"].find(
      (lang) => i18n.language.toLowerCase().indexOf(lang) === 0
    );
    return result || "en";
  }, [i18n.language]);

  const handleChangeLanguage = useCallback(
    (nextLang) => {
      i18n.changeLanguage(nextLang);
      updateSession({
        default_language: nextLang,
      });
    },
    [i18n.changeLanguage, updateSession]
  );

  if (!session) {
    return null;
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarImage src={avatar} alt={name} />
                <AvatarFallback className="rounded-lg">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">{name}</span>
                <span className="truncate text-xs">{email}</span>
              </div>
              <ChevronsUpDown className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarImage src={avatar} alt={name} />
                  <AvatarFallback className="rounded-lg">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">{name}</span>
                  <span className="truncate text-xs">{email}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem asChild>
                <Link to={buildRoute("account-profile")}>
                  <User />
                  {t("profile", "Profile")}
                </Link>
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <Languages />
                {t("language", "Language")}
              </DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent>
                  <DropdownMenuRadioGroup
                    value={language}
                    onValueChange={handleChangeLanguage}
                  >
                    <DropdownMenuRadioItem value="es">
                      {t("spanish", "Spanish")}
                    </DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="en">
                      {t("english", "English")}
                    </DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="pt">
                      {t("portuguese", "Português")}
                    </DropdownMenuRadioItem>
                  </DropdownMenuRadioGroup>
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout}>
              <LogOut />
              {t("log-out", "Log out")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
      <NoticeManager />
    </SidebarMenu>
  );
}
